import React, { lazy } from "react";
import { createRouter, createRoute, createRootRoute, RouterProvider, Outlet } from "@tanstack/react-router";
import { SiteSeo } from "./components/site-seo";

const LandingPage = lazy(() => import("./routes/index").then(m => ({ default: m.LandingPage })));
const AppPage = lazy(() => import("./routes/app").then(m => ({ default: m.AppPage })));
const AuthPage = lazy(() => import("./routes/auth").then(m => ({ default: m.AuthPage })));
const ParentsPage = lazy(() => import("./routes/parents").then(m => ({ default: m.ParentsPage })));
const SchoolsPage = lazy(() => import("./routes/schools").then(m => ({ default: m.SchoolsPage })));
const MerchantsPage = lazy(() => import("./routes/merchants").then(m => ({ default: m.MerchantsPage })));
const AboutPage = lazy(() => import("./routes/about").then(m => ({ default: m.AboutPage })));
const DemoPage = lazy(() => import("./routes/demo"));
const EcosystemPage = lazy(() => import("./routes/ecosystem").then(m => ({ default: m.EcosystemPage })));
const InsightsPage = lazy(() => import("./routes/insights").then(m => ({ default: m.InsightsPage })));
const ProductDetailPage = lazy(() => import("./components/landing/product-detail-page").then(m => ({ default: m.ProductDetailPage })));
const SimulatePage = lazy(() => import("./routes/simulate").then(m => ({ default: m.SimulatePage })));

function Lazy({ comp }: { comp: React.ComponentType }) {
  return (
    <React.Suspense fallback={<div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading…</div>}>
      {React.createElement(comp)}
    </React.Suspense>
  );
}

const rootRoute = createRootRoute({
  component: () => (
    <>
      <SiteSeo />
      <Outlet />
    </>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => <Lazy comp={LandingPage} />,
});
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: () => <Lazy comp={AppPage} />,
});
const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  component: () => <Lazy comp={AuthPage} />,
});
const parentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/parents",
  component: () => <Lazy comp={ParentsPage} />,
});
const schoolsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/schools",
  component: () => <Lazy comp={SchoolsPage} />,
});
const merchantsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/merchants",
  component: () => <Lazy comp={MerchantsPage} />,
});
const aboutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/about",
  component: () => <Lazy comp={AboutPage} />,
});
const demoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/demo",
  component: () => <Lazy comp={DemoPage} />,
});
const ecosystemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ecosystem",
  component: () => <Lazy comp={EcosystemPage} />,
});
const productDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ecosystem/$productId",
  component: () => <Lazy comp={ProductDetailPage} />,
});
const insightsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/insights",
  component: () => <Lazy comp={InsightsPage} />,
});
const simulateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/simulate",
  component: () => <Lazy comp={SimulatePage} />,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  appRoute,
  authRoute,
  parentsRoute,
  schoolsRoute,
  merchantsRoute,
  aboutRoute,
  demoRoute,
  ecosystemRoute,
  productDetailRoute,
  insightsRoute,
  simulateRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function AppRouter() {
  return <RouterProvider router={router} />;
}
